import { Form } from 'antd';
import { Deal } from 'models/Deal';
import { useDealListQuery } from 'api/deals';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { dateFormatter } from 'constants/index';
import { OrderType, SortParamsType } from 'typings';
import { TablePaginationConfig } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { FilterValue, SorterResult } from 'antd/es/table/interface';

const initialSort: SortParamsType = {
  field: null,
  order: null,
};
export const useDeals = (forDashboard?: boolean) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState<boolean>(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
  });

  const [sort, setSort] = useState<SortParamsType>(forDashboard ? { field: 'status', order: 'asc' } : initialSort);

  const [filters, setFilters] = useState<{
    clientId?: string | number;
    regionId?: string | number | (string | number)[];
    countryId?: string | number;
    status?: string | string[];
    createdDateStart?: string;
    createdDateEnd?: string;
    lastUpdatedDateStart?: string;
    lastUpdatedDateEnd?: string;
    search?: string;
  }>({});

  const { data: response, isFetching } = useDealListQuery({
    ...pagination,
    filters,
    sortField: forDashboard && !sort.field ? 'status' : sort.field,
    sortOrder: forDashboard && !sort.field ? 'asc' : sort.order,
    forDashboard,
  });

  const deals = response?.data?.content || [];
  const totalElements = response?.data?.totalElements || 0;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const routeChange = (id: number) => {
    navigate(`${ADMIN_ROUTES.deals.view}/${id}/${ADMIN_ROUTES.deals.purchaseRequest}`);
  };

  const closeDrawer = useCallback(() => {
    setFilterDrawerVisible(false);
  }, []);

  const openDrawer = () => {
    setFilterDrawerVisible(true);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Deal> | SorterResult<Deal>[]
  ) => {
    setPagination({
      page: pagination.current || 1,
      size: pagination.pageSize || 10,
    });
    const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
    const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
    const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
    setSort({
      field: sortField as string,
      order: order as OrderType,
    });
  };

  const onSearch = (value: string) => {
    setFilters({
      ...filters,
      search: value || undefined,
    });
  };

  const applyFilters = useCallback(
    (values: any) => {
      const _filters: any = {
        ...(values.clientId && { clientId: values.clientId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.regionId?.length && { regionId: values.regionId }),
        ...(values.status?.length && { statuses: values.status }),
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((prevPagination) => ({
        ...prevPagination,
        page: 1,
      }));
      closeDrawer();
    },
    [closeDrawer]
  );

  const resetFilters = () => {
    form.resetFields();
    setFilters({});
  };

  return {
    data: deals,
    isLoading,
    routeChange,
    pagination,
    onSearch,
    closeDrawer,
    openDrawer,
    filterDrawerVisible,
    form,
    handleTableChange,
    applyFilters,
    resetFilters,
    totalElements,
  };
};
